import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearSectionDetails } from "./sectionSlice";
import {
  createSection,
  getAllSection,
  getSectionByID,
  sectionImageUpload,
  updateSection,
} from "./services/section.services";

import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { getAllActiveSectors } from "../sector/services/sector.services";
import { ISector } from "../sector/sectorModel";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { generateValueLabelRange } from "../../_helper/helper";
import FormRichTextField from "../../components/formFields/FormRichTextField";

const CreateSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const SectionDetail = useAppSelector((state) => state.section.SectionDetail);
  const SectionID = useAppSelector((state) => state.drawer.data);
  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const formloading = useAppSelector((state) => state.section.formloading);

  const [formKey, setFormKey] = useState(1);
  const PositionOption = generateValueLabelRange(1, 10, "string");

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [SectionDetail]);

  useEffect(() => {
    dispatch(getAllActiveSectors());
    return () => {
      dispatch(clearSectionDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (SectionID) {
      dispatch(getSectionByID(SectionID));
    }
  }, [SectionID]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const file =
      values?.Image && typeof values?.Image !== "string"
        ? values.Image[0].getRawFile()
        : "";

    try {
      if (!SectionID) {
        const insertPayload = {
          TagLine: values?.TagLine ? values?.TagLine : "",
          Title: values?.Title ? values?.Title : "",
          SubTitle: values?.SubTitle ? values?.SubTitle : "",
          URL: values?.URL ? values?.URL : "",
          SectorID: values.SectorID ? +values.SectorID : null,
          Position: values.Position ? +values.Position : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createSection(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", file);
            ImageData.append("ID", response?.payload?.Data?.ID);

            await dispatch(sectionImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(clearSectionDetails());
          dispatch(getAllSection());
        }
      } else {
        const editPayload = {
          ID: SectionID,
          TagLine: values?.TagLine ? values?.TagLine : "",
          Title: values?.Title ? values?.Title : "",
          SubTitle: values?.SubTitle ? values?.SubTitle : "",
          URL: values?.URL ? values?.URL : "",
          SectorID: values.SectorID ? +values.SectorID : null,
          Position: values.Position ? +values.Position : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateSection(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values.Image && typeof values.Image !== "string") {
            ImageData.append("Image", file);
            ImageData.append("ID", values?.ID ? values?.ID : "");

            await dispatch(sectionImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(clearSectionDetails());
          dispatch(getAllSection());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
    dispatch(clearSectionDetails());
  };

  return (
    <>
      {formloading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        initialValues={SectionDetail}
        key={formKey}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 20,
                }}
              >
                <Typography.h4 className="m-0">
                  {SectionID ? "Update Section" : "Add Section"}
                </Typography.h4>

                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  id={"SectorID"}
                  name={"SectorID"}
                  label={"Sector"}
                  placeholder="i.e. Ahmedabad"
                  component={FormSelectionField}
                  // validator={requiredValidator}
                  // astrike={true}
                  options={SectorList?.map((sector: ISector) => {
                    return {
                      value: sector?.ID,
                      label: sector?.SectorName,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="TagLine"
                  label="Tag Line"
                  placeholder="Tag Line"
                  component={FormTextField}
                  astrike={true}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="Title"
                  label="Title"
                  placeholder="Title"
                  component={FormTextField}
                  astrike={true}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="SubTitle"
                  label="Sub Title"
                  placeholder="Sub Title"
                  component={FormRichTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem className="d-flex gap-2">
                <Field
                  wrapperClassName="w-100"
                  name="URL"
                  label="URL"
                  placeholder="URL"
                  component={FormTextField}
                  astrike={true}
                  validator={requiredValidator}
                />
                {/* </GridLayoutItem>
              <GridLayoutItem> */}
                <Field
                  wrapperClassName="w-100"
                  name="Position"
                  label="Position"
                  placeholder="Position"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={PositionOption?.map((position: any) => {
                    return {
                      value: position?.value,
                      label: position?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="Image"
                  type="file"
                  accept="image/*"
                  label="Section Image"
                  component={FormImageUpload}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  textAlign: "end",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={SectionID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formloading}
                  loading={formloading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateSection;
